import { useState } from 'react';
import type { OnlineUser } from '@/hooks/use-presence';
import { qubbase } from '@/lib/qubbase';
import type { Digest } from '@/types';
import { Alert, Avatar, Button, Icon } from './ui';

/** Who's online (realtime presence) and the task-digest edge function. */
export function SidePanel({ online, me }: { online: OnlineUser[]; me: string }) {
  const [digest, setDigest] = useState<Digest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDigest = async () => {
    setBusy(true);
    setError(null);
    // POST {QUBBASE_URL}/functions/v1/task-digest with the user's token.
    const { data, error } = await qubbase.functions.invoke<Digest>('task-digest');
    setBusy(false);
    if (error) setError(error.message);
    else setDigest(data);
  };

  return (
    <aside className="flex flex-col gap-4">
      <div className="rounded-xl border border-line bg-surface p-4">
        <h2 className="flex items-center justify-between text-sm font-semibold">
          Online now
          <span className="rounded-full bg-brand-soft px-2 py-0.5 text-xs text-brand-strong">{online.length}</span>
        </h2>
        <ul className="mt-3 flex flex-col gap-2.5">
          {online.map((u) => (
            <li key={u.user_id} className="flex items-center gap-2.5 text-sm">
              <span className="relative">
                <Avatar path={u.avatar_path} name={u.username} size={28} />
                <span className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full bg-brand ring-2 ring-surface" />
              </span>
              <span className="truncate">{u.username}</span>
              {u.user_id === me && <span className="text-xs text-muted">(you)</span>}
            </li>
          ))}
          {online.length === 0 && <li className="text-xs text-muted">Connecting…</li>}
        </ul>
        <p className="mt-3 text-xs text-muted">Open the app in another browser or a private window to see presence and live updates.</p>
      </div>

      <div className="rounded-xl border border-line bg-surface p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Icon name="bolt" className="text-brand" /> Task digest
        </h2>
        <p className="mt-1 text-xs text-muted">Runs the “task-digest” edge function, which reads your tasks with your token.</p>
        <Button className="mt-3 w-full" onClick={runDigest} loading={busy}>
          <Icon name="sparkle" /> Summarise my tasks
        </Button>
        {error && (
          <div className="mt-3">
            <Alert>{error}</Alert>
          </div>
        )}
        {digest && (
          <div className="mt-3 rounded-lg bg-sunken p-3 text-sm">
            <p>{digest.message}</p>
            {digest.next.length > 0 && (
              <ol className="mt-2 list-inside list-decimal text-xs text-soft">
                {digest.next.map((t, i) => (
                  <li key={i} className="truncate">
                    {t}
                  </li>
                ))}
              </ol>
            )}
            <p className="mt-2 text-[11px] text-muted">Generated {new Date(digest.generated_at).toLocaleTimeString()}</p>
          </div>
        )}
      </div>
    </aside>
  );
}
